<template>
  <div class="p-4" data-private>
    <div class="flex items-start justify-between mb-4">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Supplemental Data Flat File</h2>
        <p class="text-sm text-gray-500">
          Care gaps closed by the quality team, formatted to the
          <span class="font-medium">{{ payer }}</span> supplemental-data file layout.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <select
          v-model="payer"
          class="text-sm border border-gray-300 rounded px-2 py-1.5"
          @change="onPayerChange"
        >
          <option v-for="p in payers" :key="p" :value="p">{{ p }}</option>
        </select>
        <select
          v-model="layout"
          class="text-sm border border-gray-300 rounded px-2 py-1.5"
          @change="loadRows"
        >
          <option v-for="o in layoutOptions" :key="o.value" :value="o.value">
            {{ o.label }}
          </option>
        </select>
        <button
          class="text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded px-3 py-1.5 disabled:opacity-50"
          :disabled="loading || rows.length === 0"
          @click="exportFile('txt')"
        >
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
          Export
        </button>
        <button
          class="text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded px-3 py-1.5 disabled:opacity-50"
          :disabled="loading || rows.length === 0"
          title="Review copy for validating against source documents. Not the submission format."
          @click="exportFile('xlsx')"
        >
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
          Excel
        </button>
      </div>
    </div>

    <div v-if="loading" class="py-16 text-center text-gray-400">Loading…</div>

    <div v-else-if="rows.length === 0" class="py-16 text-center">
      <p class="text-gray-500 font-medium">No submittable rows for {{ payer }}.</p>
      <div v-if="skipped" class="text-xs text-gray-500 mt-3 inline-block text-left">
        <p v-if="skipped.not_closed">
          · <span class="font-medium">{{ skipped.not_closed }}</span>
          chart review(s) did not close the gap (e.g. patient not found in EHR)
        </p>
        <p v-if="skipped.no_code" class="text-amber-600">
          · <span class="font-medium">{{ skipped.no_code }}</span>
          closure(s) have no code in the Vytalize quality-codes file
        </p>
        <p v-if="skipped.no_dos">
          · <span class="font-medium">{{ skipped.no_dos }}</span> missing date of service
        </p>
        <p v-if="skipped.no_identity">
          · <span class="font-medium">{{ skipped.no_identity }}</span>
          missing member ID, name or DOB
        </p>
        <p v-if="skipped.wrong_layout" class="text-gray-400">
          · {{ skipped.wrong_layout }} belong to the other layout for this payer
        </p>
      </div>
    </div>

    <div v-else>
      <div class="text-xs text-gray-500 mb-2">
        {{ rows.length }} rows · {{ columns.length }} columns ({{ payer }} {{ layout }} layout)
        <span v-if="skippedNote" class="text-amber-600 ml-2">· excluded: {{ skippedNote }}</span>
      </div>
      <div class="overflow-auto border border-gray-200 rounded" style="max-height: calc(100vh - 260px)">
        <table class="min-w-full text-xs">
          <thead class="sticky top-0 bg-slate-800 text-white">
            <tr>
              <th
                v-for="col in columns"
                :key="col"
                class="text-left font-medium px-2 py-2 whitespace-nowrap"
              >
                {{ col }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in rows"
              :key="i"
              :class="i % 2 ? 'bg-gray-50' : 'bg-white'"
            >
              <td
                v-for="col in columns"
                :key="col"
                class="px-2 py-1 whitespace-nowrap text-gray-700"
              >
                {{ row[col] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
// Each payer has its own published file layout. Endpoints return the spec
// column list (plain strings, in spec order) plus rows keyed by those names.
const PAYER_SPECS = {
  Anthem: {
    endpoint: '/api/quality/supplemental-flat-file/',
    exportEndpoint: '/api/quality/supplemental-flat-file-export/',
    layouts: [
      { value: 'EMR', label: 'EMR / Encounter file' },
      { value: 'LAB', label: 'Lab file' },
    ],
  },
  Aetna: {
    endpoint: '/api/quality/aetna-flat-file/',
    exportEndpoint: '/api/quality/aetna-flat-file-export/',
    layouts: [
      { value: 'MEDICAL', label: 'Medical file' },
      { value: 'LAB', label: 'Lab file' },
    ],
  },
  Fidelis: {
    endpoint: '/api/quality/fidelis-flat-file/',
    exportEndpoint: '/api/quality/fidelis-flat-file-export/',
    layouts: [
      { value: 'SERVICE', label: 'Service file' },
      { value: 'LAB', label: 'Lab file' },
    ],
    // Wellcare rejects the file outright on a wrong name. Fidelis assigned
    // Vytalize VYH_NY_SERVICE_* on the 2026-08-18 test review — no [CNC|ESD]
    // segment, despite the SuDS guide publishing one.
    filename: (layout, stamp) => `VYH_NY_${layout === 'LAB' ? 'LAB' : 'SERVICE'}_${stamp}.txt`,
  },
};

export default {
  name: 'Quality_FlatFile',
  data() {
    return {
      loading: false,
      columns: [],
      rows: [],
      skipped: null,
      payer: 'Aetna',
      payers: Object.keys(PAYER_SPECS),
      layout: 'MEDICAL',
    };
  },
  computed: {
    spec() {
      return PAYER_SPECS[this.payer];
    },
    layoutOptions() {
      return this.spec.layouts;
    },
    skippedTotal() {
      if (!this.skipped) return 0;
      return Object.values(this.skipped).reduce((a, b) => a + (b || 0), 0);
    },
    skippedNote() {
      const s = this.skipped;
      if (!s) return '';
      const parts = [];
      if (s.no_code) parts.push(`${s.no_code} no sourced code`);
      if (s.no_dos) parts.push(`${s.no_dos} no date of service`);
      if (s.no_identity) parts.push(`${s.no_identity} incomplete member identity`);
      return parts.join(', ');
    },
  },
  mounted() {
    this.loadRows();
  },
  methods: {
    onPayerChange() {
      // Reset to the first layout this payer publishes.
      this.layout = this.spec.layouts[0].value;
      this.loadRows();
    },
    async loadRows() {
      this.loading = true;
      try {
        const res = await this.$axios.post(this.spec.endpoint, { layout: this.layout });
        this.columns = res.data.columns || [];
        this.rows = res.data.rows || [];
        this.skipped = res.data.skipped || null;
      } catch (e) {
        this.columns = [];
        this.rows = [];
        this.skipped = null;
      } finally {
        this.loading = false;
      }
    },
    async exportFile(format = 'txt') {
      try {
        const res = await this.$axios.post(
          this.spec.exportEndpoint,
          { layout: this.layout, format },
          { responseType: 'blob' },
        );
        // The server sets the payer-compliant name; use it when the browser
        // exposes the header, else fall back to the payer's own format.
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const cd = res.headers && (res.headers['content-disposition']
          || (res.headers.get && res.headers.get('content-disposition')));
        const m = cd && /filename="?([^"]+)"?/.exec(cd);
        const fallback = format === 'xlsx'
          ? `${this.payer}_${this.layout}_${stamp}_REVIEW.xlsx`
          : (this.spec.filename
            ? this.spec.filename(this.layout, stamp)
            : `${this.payer}_${this.layout}_${stamp}.txt`);
        const fname = m ? m[1] : fallback;
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        // surfaced via the empty-state; nothing to do here
      }
    },
  },
};
</script>
